// Accrues the minutes a learner actually spends in the app and flushes them
// to POST /auth/activity, which feeds the admin "Total Time" column.
//
// The naive version of this (wall-clock between ticks) reported nonsense: a
// tab left open overnight counted as study time, and flushing on `hidden`
// without resetting the clock billed the same span twice. So:
//   - only visible time accrues,
//   - only time within IDLE_LIMIT_MS of a real interaction accrues, and
//   - the leftover seconds carry to the next flush instead of being
//     truncated away.
//
// Mobile twin: mobile/src/services/sessionTimeTracker.ts — keep both in step.

const FLUSH_INTERVAL_MS = 5 * 60 * 1000;
const IDLE_LIMIT_MS = 5 * 60 * 1000;
const TICK_MS = 15 * 1000;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

export function installSessionTimeTracker({ userId, sendMinutes }) {
  if (!userId || typeof sendMinutes !== 'function') return () => {};

  let accruedMs = 0;
  let lastTickAt = Date.now();
  let lastInteractionAt = Date.now();

  const isActive = () => (
    !document.hidden && (Date.now() - lastInteractionAt) <= IDLE_LIMIT_MS
  );

  const accrue = () => {
    const now = Date.now();
    const elapsed = now - lastTickAt;
    lastTickAt = now;
    // A suspended/throttled timer can come back with a huge gap. Trust the
    // tick cadence over the clock so a sleeping laptop is not study time.
    if (isActive() && elapsed > 0) accruedMs += Math.min(elapsed, TICK_MS * 2);
  };

  const flush = () => {
    accrue();
    const minutes = Math.floor(accruedMs / 60000);
    if (minutes <= 0) return;
    accruedMs -= minutes * 60000;
    sendMinutes(minutes);
  };

  const markInteraction = () => { lastInteractionAt = Date.now(); };

  const handleVisibility = () => {
    // Close out the visible span before the state flips, then restart the
    // clock so backgrounded time is never counted or re-counted.
    accrue();
    lastTickAt = Date.now();
    if (!document.hidden) markInteraction();
  };

  const tick = setInterval(accrue, TICK_MS);
  const flushTimer = setInterval(flush, FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', handleVisibility);
  INTERACTION_EVENTS.forEach((name) => {
    window.addEventListener(name, markInteraction, { passive: true });
  });

  return () => {
    flush();
    clearInterval(tick);
    clearInterval(flushTimer);
    document.removeEventListener('visibilitychange', handleVisibility);
    INTERACTION_EVENTS.forEach((name) => {
      window.removeEventListener(name, markInteraction);
    });
  };
}
